import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField,
  InputAdornment,
  Button,
  Badge,
  alpha,
  Stack,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Hotel as HotelIcon,
  Description as DescriptionIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Assessment as ReportsIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  CloudDownload as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Help as HelpIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '@features/auth/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../contexts/ThemeContext';
import SidebarNavigation from './SidebarNavigation';

// Drawer width for desktop view
const DRAWER_WIDTH = 350;

// Styled components
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
    : theme.palette.background.default,
  color: theme.palette.mode === 'light' ? theme.palette.text.primary : '#fff',
  backdropFilter: 'blur(10px)',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Default collapsed state
  marginLeft: theme.spacing(7), // 56px for collapsed drawer
  width: `calc(100% - ${theme.spacing(7)})`,
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%',
  },
  ...(open && {
    marginLeft: DRAWER_WIDTH, // Use full drawer width for clean alignment
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      width: '100%',
    },
  }),
}));

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      width: DRAWER_WIDTH,
      '& .MuiDrawer-paper': {
        width: DRAWER_WIDTH,
        boxSizing: 'border-box',
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        background: theme.palette.mode === 'light'
          ? `linear-gradient(180deg, #1a365d 0%, #2d3748 100%)`
          : theme.palette.background.paper,
        color: '#fff',
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
      },
    }),
    ...(!open && {
      '& .MuiDrawer-paper': {
        width: theme.spacing(7),
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        background: theme.palette.mode === 'light'
          ? `linear-gradient(180deg, #1a365d 0%, #2d3748 100%)`
          : theme.palette.background.paper,
        color: '#fff',
        overflowX: 'hidden',
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        [theme.breakpoints.down('sm')]: {
          width: 0,
        },
      },
    }),
  }),
);

const MainContent = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2.5),
  },
  // Remove all margins - AppBar handles positioning
  marginLeft: 0,
  width: '100%',
  maxWidth: 'none',
  overflowX: 'hidden',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.01)} 0%, ${theme.palette.background.default} 100%)`
    : theme.palette.background.default,
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.5, 1.5),
  minWidth: 'auto',
  '&:hover': {
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
}));

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Close drawer for mobile view by default
  React.useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setOpen(false);
    }
  };

  const handleSearch = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
    }
  };

  // Mock notifications
  const notifications = [
    { id: 1, title: 'New Market Report Available', time: '5 min ago', type: 'report' },
    { id: 2, title: 'Hotel Valuation Completed', time: '1 hour ago', type: 'valuation' },
    { id: 3, title: 'Weekly Portfolio Update', time: '2 hours ago', type: 'update' },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Simplified AppBar - Much Cleaner */}
      <StyledAppBar position="fixed" open={open}>
        <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, sm: 3 } }}>
          {/* Menu Toggle */}
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          {/* App Logo - Simplified */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              color: 'inherit',
              mr: 3,
            }}
          >
            Valuate
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Only Essential Items */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Search - Desktop Only */}
            <TextField
              variant="outlined"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleSearch}
              size="small"
              sx={{
                width: { xs: 0, md: 280 },
                display: { xs: 'none', md: 'block' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.1),
                  border: 'none',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.15),
                  },
                  '&.Mui-focused': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.2),
                  },
                  '& fieldset': { border: 'none' },
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Mobile Search Icon */}
            <IconButton
              color="inherit"
              sx={{ display: { xs: 'flex', md: 'none' } }}
              onClick={() => navigate('/search')}
            >
              <SearchIcon />
            </IconButton>

            {/* Notifications - Simple */}
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
            >
              <Badge badgeContent={notifications.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Profile - Simple */}
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.9rem',
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        id="notifications-menu"
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 320,
            maxHeight: 400,
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>
        {notifications.map((notification) => (
          <MenuItem key={notification.id} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}` }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {notification.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {notification.time}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <MenuItem onClick={() => { navigate('/notifications'); handleNotificationMenuClose(); }}>
          <Typography variant="body2" color="primary" sx={{ textAlign: 'center', width: '100%' }}>
            View All Notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {user?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || 'user@example.com'}
          </Typography>
        </Box>
        <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => { handleNavigation('/settings'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => { toggleTheme(); handleProfileMenuClose(); }}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          <ListItemIcon>
            {mode === 'dark' ? (
              <LightModeIcon
                fontSize="small"
                sx={{ color: theme.palette.warning.main }}
              />
            ) : (
              <DarkModeIcon
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            )}
          </ListItemIcon>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="body2">
              Theme
            </Typography>
            <Chip
              label={mode === 'dark' ? 'Dark' : 'Light'}
              size="small"
              variant="outlined"
              sx={{
                ml: 1,
                fontSize: '0.7rem',
                height: 20,
                color: mode === 'dark' ? theme.palette.primary.main : theme.palette.warning.main,
                borderColor: mode === 'dark' ? theme.palette.primary.main : theme.palette.warning.main
              }}
            />
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { handleNavigation('/help'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          Help & Support
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Sidebar/Drawer */}
      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: 0
        }}>
          {/* Sidebar content with navigation */}
          <SidebarNavigation
            open={open}
            onNavigate={handleNavigation}
          />
        </Box>
      </StyledDrawer>

      {/* Main content */}
      <MainContent open={open} sx={{ marginTop: '70px' }}>
        {children}
      </MainContent>
    </Box>
  );
};

export default MainLayout;